import React, { useCallback, useEffect, useState } from "react"
import { useToaster } from "@hellocontento/maillard"
import { Flex } from "rebass/styled-components"

import Loader from "components/common/loading/Loader"
import { callApi } from "utils/ContentoApi"
import type { GenerationOptions, ProfileOption, RecentCustomInstruction, SourceInput } from "../types"
import type { PlanningDefaults } from "../planning"
import { fetchPlanningDefaults } from "../planningApi"
import { fetchRecentCustomInstructions } from "../customInstructionsApi"
import { useSupportedChannels } from "../useSupportedChannels"
import { useCreateGeneration } from "../useCreateGeneration"
import AddSourceModal from "./AddSourceModal"
import BatchBox from "./BatchBox"

import * as styles from "../styles"

type PostsTabProps = {
  accountId: string
  profiles?: ProfileOption[]
  isSourceModalOpen: boolean
  onCloseSourceModal: () => void
  onGoToProfile?: (profileId: string) => void
  // A batch created from the search page, to surface immediately on first render.
  initialPendingBatchId?: string | null
  onProfilesChanged: () => void // the picker's __new__ option creates a profile too
}

const PostsTab: React.FC<PostsTabProps> = ({ accountId, profiles = [], isSourceModalOpen, onCloseSourceModal, onGoToProfile, initialPendingBatchId, onProfilesChanged }) => {
  const addToast = useToaster()
  const channels = useSupportedChannels()
  const { createBatch } = useCreateGeneration(accountId)
  const [batchIds, setBatchIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pendingBatchId, setPendingBatchId] = useState<string | null>(initialPendingBatchId ?? null)
  const [planningDefaults, setPlanningDefaults] = useState<PlanningDefaults | null>(null)
  const [recentCustomInstructions, setRecentCustomInstructions] = useState<RecentCustomInstruction[]>([])

  const fetchBatchIds = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const { postBatches } = await callApi({ method: "get", url: `content-factory/accounts/${accountId}/postBatches` }, true, true)
      setBatchIds(postBatches.map((postBatch: { id: string }) => postBatch.id))
    } catch (err) {
      console.error("Failed to fetch post batches", { accountId, err })
      addToast("Failed to fetch post batches.", "error")
      setErrorMessage("Failed to fetch post batches.")
    } finally {
      setIsLoading(false)
    }
  }, [accountId, addToast])

  useEffect(() => {
    fetchBatchIds()
  }, [fetchBatchIds])

  // Fetched per opening: the plan or the week may have changed since the last batch was created.
  useEffect(() => {
    if (!isSourceModalOpen) return
    fetchPlanningDefaults(accountId)
      .then(setPlanningDefaults)
      .catch((err) => console.error("Failed to fetch planning defaults", { accountId, err }))
    fetchRecentCustomInstructions(accountId)
      .then(setRecentCustomInstructions)
      .catch((err) => console.error("Failed to fetch recent custom instructions", { accountId, err }))
  }, [accountId, isSourceModalOpen])

  // Surface a freshly-created batch right away (the mount fetch may have raced it).
  // Runs once per new id, so deleting it doesn't re-add it.
  useEffect(() => {
    if (pendingBatchId) {
      setBatchIds((prev) => prev.includes(pendingBatchId) ? prev : [pendingBatchId, ...prev])
    }
  }, [pendingBatchId])

  const handleBatchDeleted = useCallback((batchId: string) => {
    setBatchIds((prev) => prev.filter((id) => id !== batchId))
  }, [])

  const handleSourceSubmit = async (source: SourceInput, options: GenerationOptions) => {
    const postBatchId = await createBatch(source, options)
    if (postBatchId) {
      setPendingBatchId(postBatchId)
      onProfilesChanged()
    }
    onCloseSourceModal()
  }

  if (isLoading) {
    return (
      <div style={{ position: "relative", height: "200px" }}>
        <Loader location="center" size={48} />
      </div>
    )
  }

  return (
    <>
      {errorMessage ? (
        <Flex justifyContent="center">
          <styles.InlineSubtitle>{errorMessage}</styles.InlineSubtitle>
        </Flex>
      ) : batchIds.length === 0 ? (
        <Flex justifyContent="center">
          <styles.InlineSubtitle>No post batches yet. Generate one to see posts here.</styles.InlineSubtitle>
        </Flex>
      ) : (
        <Flex flexDirection="column" mt={40} style={{ gap: "16px" }}>
          {batchIds.map((batchId) => (
            <BatchBox
              key={batchId}
              accountId={accountId}
              batchId={batchId}
              initialExpanded={batchId === pendingBatchId}
              onDeleted={() => handleBatchDeleted(batchId)}
              onGoToProfile={onGoToProfile}
            />
          ))}
        </Flex>
      )}

      <AddSourceModal
        isOpen={isSourceModalOpen}
        onClose={onCloseSourceModal}
        onSubmit={handleSourceSubmit}
        profiles={profiles}
        channels={channels}
        planningDefaults={planningDefaults}
        recentCustomInstructions={recentCustomInstructions}
        mode="posts"
      />
    </>
  )
}

export default PostsTab
